import React, { useEffect, useState } from 'react';
import {
  Autocomplete, Button, Chip, MenuItem, Stack, TextField, Tooltip,
} from '@mui/material';
import iziToast from 'izitoast';

import { addStudentToGroup, getGroups } from '../../actions/journal';
import { internalServerError } from '../../utils/consts/pathRoutes';
import { PERMISSIONS, useCurrentUser } from '../../utils/currentUser';

/**
 * Поля «1 семестр» и «2 семестр» в карточке слушателя — требование ТЗ 3.1.
 *
 * Выбор идёт из уже созданных групп, а не из фиксированного списка 1…100: на встрече
 * заказчик поправился, что группы кто-то создаёт заранее.
 *
 * Это обратный путь к экрану группы: распределить слушателя можно и отсюда, и из журнала.
 */
const StudentGroupAssign = ({ studentId, periods, onAssigned }) => {
  const { can } = useCurrentUser();
  const canManage = can(PERMISSIONS.JOURNAL_MANAGE);

  const [groups, setGroups] = useState([]);
  const [pending, setPending] = useState({ 1: null, 2: null });
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!canManage) return;

    getGroups({ status: 'Набор' })
      .then(setGroups)
      .catch(() => setGroups([]));
  }, [canManage]);

  if (!canManage) return null;

  const assign = (semester) => {
    const target = pending[semester];
    if (!target) return;

    setSaving(semester);
    addStudentToGroup(target.id, { student_id: studentId })
      .then(() => {
        iziToast.success({ message: `Слушатель добавлен в ${target.name}`, position: 'topRight' });
        setPending((prev) => ({ ...prev, [semester]: null }));
        onAssigned();
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setSaving(null));
  };

  const renderSemester = (semester) => {
    const current = periods.filter((period) => period.is_active && period.semester === semester);
    const options = groups
      .filter((group) => group.semester === semester)
      .map((group) => ({
        id: group.id,
        name: group.name,
        label: `${group.name} · ${group.students_count}/${group.capacity} мест занято`,
      }));

    return (
      <Stack key={semester} direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <span className="journal-muted" style={{ minWidth: 82 }}>{semester} семестр:</span>

        {current.length > 0
          ? current.map((period) => (
            <Chip key={period.membership_id} size="small" label={period.group_name} />
          ))
          : <span className="journal-muted">не распределён</span>}

        <Autocomplete
          options={options}
          value={pending[semester]}
          onChange={(event, value) => setPending((prev) => ({ ...prev, [semester]: value }))}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          sx={{ minWidth: 280 }}
          renderInput={(params) => (
            <TextField {...params} label={`Добавить в группу ${semester} семестра`} />
          )}
        />

        <Tooltip title="Во 2 семестре слушатель может учиться одновременно в РЯ и профильной группе">
          <span>
            <Button
              type="button" variant="outlined" size="small"
              disabled={!pending[semester] || saving === semester}
              onClick={() => assign(semester)}
            >
              Добавить
            </Button>
          </span>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {[1, 2].map(renderSemester)}
    </Stack>
  );
};

export default StudentGroupAssign;

/** Уровень владения языком — поле карточки, по нему распределяют в группы. */
export const LANGUAGE_LEVELS = ['Нулевой', 'A1', 'A2', 'B1', 'B2', 'C1'];

export const LanguageLevelField = ({ value, comment, date, editMode, textFieldStyle }) => (
  <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
    <TextField
      select label="Уровень русского языка" name="language_level" defaultValue={value || ''}
      variant="outlined" color="warning" margin="normal" size="small" sx={{ width: 220 }}
      disabled={editMode} inputProps={textFieldStyle} InputLabelProps={textFieldStyle}
    >
      <MenuItem value="">Не определён</MenuItem>
      {LANGUAGE_LEVELS.map((level) => (
        <MenuItem key={level} value={level}>{level}</MenuItem>
      ))}
    </TextField>

    <TextField
      label="Дата тестирования" name="language_level_date" type="date"
      defaultValue={date ? String(date).slice(0, 10) : ''}
      variant="outlined" color="warning" margin="normal" size="small" sx={{ width: 200 }}
      disabled={editMode} InputLabelProps={{ ...textFieldStyle, shrink: true }}
    />

    <TextField
      label="Комментарий по уровню" name="language_level_comment" defaultValue={comment || ''}
      variant="outlined" color="warning" margin="normal" size="small" sx={{ width: 320 }}
      disabled={editMode} inputProps={textFieldStyle} InputLabelProps={textFieldStyle}
    />
  </Stack>
);
